import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { DefaultTheme } from "@mui/styles";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteHighlight } from "../AutocompleteHighlight";
import { managedSurveyAssetType } from "../constants/managedSurveyAssetTypes";
import { ValidationMessage } from "../ValidationMessage";
import { AssetTypesDropdownProps } from "./AssetTypesDropdown";

const useStyles = makeStyles((theme: DefaultTheme) => ({
  dropdownIcon: {
    color: theme.palette.midGrey.main,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: "100%",
    alignItems: "right",
    paddingRight: 0,
  },
  validationMessage: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: "5px",
    zIndex: 1,
  },
}));

const assetTypes = Object.entries(managedSurveyAssetType).map(([value, label]) => ({ value, label }));

export default function PrimeReactAssetTypesDropdown(props: AssetTypesDropdownProps) {
  const { selectedAssetType, minWidth, onAssetTypeChange, error } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  function handleChange(_e: React.SyntheticEvent, value: { value: string; label: string } | null) {
    onAssetTypeChange(value?.value ?? "");
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <Box onClick={() => setOpen(!open)}>
        <Autocomplete
          autoHighlight
          blurOnSelect
          id="assetTypeDropdown"
          open={open}
          onClose={() => setOpen(false)}
          value={assetTypes.find((option) => option.value === selectedAssetType) ?? null}
          options={assetTypes.filter((option) => option.label !== managedSurveyAssetType.Unknown)}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              placeholder={t("common.select") + t("asset.assetType")}
              sx={{
                input: {
                  color: theme.palette.midGrey.main,
                  "&::placeholder": { opacity: 1 },
                },
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: theme.palette.blue.main },
                },
                "& .MuiInputBase-root": {
                  paddingRight: "6px !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end" style={{ alignItems: "center", height: 16, paddingRight: 0 }}>
                    <i className={clsx("pi pi-chevron-down", classes.dropdownIcon)} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderOption={(props, option, { inputValue }) => (
            <AutocompleteHighlight
              renderProps={props}
              optionLabel={t(`assetTypes.${option.label}`)}
              inputValue={inputValue}
            />
          )}
          size="small"
          sx={{ minWidth: minWidth, width: "100%", bgcolor: theme.palette.primary.contrastText }}
          onChange={handleChange}
          getOptionLabel={(option) => t(`assetTypes.${option.label}`)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          PaperComponent={(props) => <Paper {...props} sx={{ boxShadow: 6 }} />}
        />
      </Box>
      {error && (
        <div className={classes.validationMessage}>
          <ValidationMessage invalidMessage={error} validationType="error" showIcon />
        </div>
      )}
    </Stack>
  );
}
